import React from 'react';
import styled from 'styled-components';
import MapSection from './MapSection';
import { Sparkles, Heart, MapPin, Phone, Mail, Facebook, Instagram, Twitter } from 'lucide-react';

// Styled Components
const FooterWrapper = styled.footer`
  background: black;
  color: white;
  position: relative;
  overflow: hidden;
`;

const FooterTop = styled.div`
  background: linear-gradient(135deg, #111827 0%, #1f2937 100%);
  padding: 3rem 0;
  border-bottom: 1px solid rgba(147, 51, 234, 0.2);
`;

const FooterMain = styled.div`
  padding: 2rem 0;
`;

const FooterBottom = styled.div`
  background: #000;
  padding: 1.5rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 640px) {
    padding: 0 1.5rem;
  }

  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const FooterGrid = styled.div`
  display: grid;
  gap: 2rem;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 3rem;
  }
`;

const FooterColumn = styled.div`
  animation: fadeInUp 0.6s ease-out;
  animation-delay: ${props => props.$delay || '0s'};
  animation-fill-mode: backwards;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const LogoSection = styled.div``;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;

  svg {
    width: 32px;
    height: 32px;
    color: #c084fc;
  }
`;

const LogoText = styled.span`
  font-size: 1.5rem;
  font-weight: bold;
  background: linear-gradient(135deg, white 0%, #c084fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  color: #9ca3af;
  line-height: 1.7;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
`;

const ContactInfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ContactInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #9ca3af;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &:hover {
    color: #c084fc;
    transform: translateX(5px);
  }

  svg {
    width: 16px;
    height: 16px;
    color: #9333ea;
    flex-shrink: 0;
  }
`;

const ColumnTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: bold;
  margin-bottom: 1.5rem;
  color: white;
  position: relative;
  padding-bottom: 0.5rem;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 2px;
    background: linear-gradient(90deg, #9333ea 0%, #ec4899 100%);
  }
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const LinkItem = styled.li``;

const FooterLink = styled.a`
  color: #9ca3af;
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  display: inline-block;

  &:hover {
    color: #c084fc;
    transform: translateX(5px);
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(147, 51, 234, 0.1);
  border: 1px solid rgba(147, 51, 234, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  cursor: pointer;

  svg {
    width: 18px;
    height: 18px;
    color: #c084fc;
  }

  &:hover {
    background: linear-gradient(135deg, #9333ea 0%, #ec4899 100%);
    border-color: transparent;
    transform: translateY(-5px);

    svg {
      color: white;
    }
  }
`;

const Newsletter = styled.div`
  margin-top: 1.5rem;
`;

const NewsletterText = styled.p`
  color: #9ca3af;
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 0.5rem;
`;

const NewsletterInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: #1f2937;
  border: 1px solid #374151;
  color: white;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #9333ea;
    background: #374151;
  }

  &::placeholder {
    color: #6b7280;
  }
`;

const NewsletterButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #9333ea 0%, #ec4899 100%);
  border: none;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(147, 51, 234, 0.5);
  }

  &:active {
    transform: translateY(0);
  }
`;

const BottomContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  text-align: center;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    text-align: left;
  }
`;

const Copyright = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;

  @media (min-width: 768px) {
    justify-content: flex-start;
  }
`;

const HeartIcon = styled(Heart)`
  width: 14px;
  height: 14px;
  color: #ec4899;
  fill: #ec4899;
  animation: heartbeat 1.5s infinite;

  @keyframes heartbeat {
    0%, 100% { transform: scale(1); }
    10%, 30% { transform: scale(1.1); }
    20%, 40% { transform: scale(1); }
  }
`;

const BottomLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  justify-content: center;

  @media (min-width: 768px) {
    justify-content: flex-end;
  }
`;

const BottomLink = styled.a`
  color: #6b7280;
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 0.3s ease;

  &:hover {
    color: #c084fc;
  }
`;

const BackToTop = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #9333ea 0%, #ec4899 100%);
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  z-index: 40;
  display: ${props => props.$show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 15px -3px rgba(147, 51, 234, 0.5);
  }

  &:active {
    transform: translateY(-2px);
  }
`;

// Component
const Footer = () => {
  const [showBackToTop, setShowBackToTop] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    alert('Cảm ơn bạn đã đăng ký nhận tin!');
  };

  const currentYear = new Date().getFullYear();

  return (
    <>
      <FooterWrapper>
        {/* Footer Top */}
        <FooterTop>
          <Container>
            <FooterGrid>
              {/* Company Info */}
              <FooterColumn $delay="0.1s">
                <LogoSection>
                  <Logo>
                    <Sparkles />
                    <LogoText>KM NAIL HAIR SALON</LogoText>
                  </Logo>
                  <Description>
                    Mang đến vẻ đẹp hoàn hảo và sự tự tin cho bạn với dịch vụ chăm sóc tóc và móng chuyên nghiệp, tận tâm.
                  </Description>
                  <ContactInfoList>
                    <ContactInfoItem>
                      <MapPin />
                      <span>43/2C Bà Điểm 5, Bà Điểm,Thành phố Hồ Chí Minh</span>
                    </ContactInfoItem>
                    <ContactInfoItem>
                      <Phone />
                      <span>096 8694 520</span>
                    </ContactInfoItem>
                    <ContactInfoItem>
                      <Mail />
                      <span>kmsalon2001@gmail.com</span>
                    </ContactInfoItem>
                  </ContactInfoList>
                </LogoSection>
              </FooterColumn>

              {/* Quick Links */}
              <FooterColumn $delay="0.2s">
                <ColumnTitle>Liên Kết</ColumnTitle>
                <LinkList>
                  <LinkItem>
                    <FooterLink href="#trang chủ">Trang chủ</FooterLink>
                  </LinkItem>
                  <LinkItem>
                    <FooterLink href="#dịch vụ">Dịch vụ</FooterLink>
                  </LinkItem>
                  <LinkItem>
                    <FooterLink href="#gallery">Gallery</FooterLink>
                  </LinkItem>
                  <LinkItem>
                    <FooterLink href="#đánh giá">Đánh giá</FooterLink>
                  </LinkItem>
                  <LinkItem>
                    <FooterLink href="#liên hệ">Liên hệ</FooterLink>
                  </LinkItem>
                </LinkList>
              </FooterColumn>

              {/* Services */}
              <FooterColumn $delay="0.3s">
                <ColumnTitle>Dịch Vụ</ColumnTitle>
                <LinkList>
                  <LinkItem>
                    <FooterLink href="#dịch vụ">Hair Styling</FooterLink>
                  </LinkItem>
                  <LinkItem>
                    <FooterLink href="#dịch vụ">Nail Art</FooterLink>
                  </LinkItem>
                  <LinkItem>
                    <FooterLink href="#dịch vụ">Spa & Treatment</FooterLink>
                  </LinkItem>
                  <LinkItem>
                    <FooterLink href="#dịch vụ">Hair Coloring</FooterLink>
                  </LinkItem>
                  <LinkItem>
                    <FooterLink href="#dịch vụ">Nail Extension</FooterLink>
                  </LinkItem>
                </LinkList>
              </FooterColumn>

              {/* Newsletter & Social */}
              <FooterColumn $delay="0.4s">
                <ColumnTitle>Kết Nối</ColumnTitle>
                <SocialLinks>
                  <SocialLink target="_blank" href="https://www.facebook.com/share/1Y366R6Vy1/?mibextid=wwXIfr" aria-label="Facebook">
                    <Facebook />
                  </SocialLink>
                  <SocialLink target="_blank" href="https://www.instagram.com/kmnailhairsalon" aria-label="Instagram">
                    <Instagram />
                  </SocialLink>
                </SocialLinks>

                <Newsletter>
                  <MapSection/>
                </Newsletter>
              </FooterColumn>
            </FooterGrid>
          </Container>
        </FooterTop>

        {/* Footer Bottom */}
        <FooterBottom>
          <Container>
            <BottomContent>
              <Copyright>
                © {currentYear} KM Hair & Nail Salon. Made with <HeartIcon /> in Vietnam
              </Copyright>
              <BottomLinks>
                <BottomLink href="#">Privacy Policy</BottomLink>
                <BottomLink href="#">Terms of Service</BottomLink>
                <BottomLink href="#">Sitemap</BottomLink>
              </BottomLinks>
            </BottomContent>
          </Container>
        </FooterBottom>
      </FooterWrapper>

      {/* Back to Top Button */}
      <BackToTop $show={showBackToTop} onClick={scrollToTop}>
        ↑
      </BackToTop>
    </>
  );
};

export default Footer;